/**
 * Chép một chuỗi vào clipboard — trả `true` khi chép được.
 *
 * ## ⚠️ Vì sao KHÔNG gọi thẳng `navigator.clipboard`
 *
 * Cùng bẫy với `crypto.randomUUID` (`lib/uuid.ts`) và `crypto.subtle`
 * (`lib/sha256.ts`): `navigator.clipboard` chỉ có ở **secure context** —
 * `https://` hoặc `localhost`. Mở app bằng IP LAN (`http://192.168.10.3:5210`,
 * cách bình thường để thử trên máy khác) thì nó là `undefined`: gọi thẳng là
 * ném `TypeError`, còn viết `navigator.clipboard?.writeText(...)` thì nút bấm
 * **im lặng không làm gì** — còn khó hiểu hơn một lỗi.
 *
 * Dự phòng bằng `<textarea>` ẩn + `document.execCommand('copy')`: API cũ, đã
 * bị đánh dấu lỗi thời nhưng vẫn chạy ở mọi trình duyệt và **không** cần
 * secure context. Hỏng cả hai thì trả `false` để người gọi nói "hãy chọn rồi
 * chép tay" — không bao giờ ném.
 */
export async function copyText(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Bị từ chối quyền (tab không có focus, chính sách trình duyệt): thử đường cũ.
    }
  }
  return copyWithTextarea(text);
}

function copyWithTextarea(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const area = document.createElement('textarea');
  area.value = text;
  // Ngoài khung nhìn nhưng vẫn chọn được — `display: none` thì không chọn được.
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '-1000px';
  area.style.opacity = '0';
  document.body.appendChild(area);
  const previous = document.activeElement as HTMLElement | null;
  try {
    area.select();
    area.setSelectionRange(0, text.length);
    return typeof document.execCommand === 'function' && document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
    previous?.focus?.();
  }
}
